import { useContext, type HTMLAttributes, type ReactNode } from 'react';
import { CardBareContext } from './card-bare-context';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  /** Rule under the header row, separating it from `children`. Opt-in since
   * most `Card` consumers (e.g. the account cards) don't want it - only pass this
   * where the divider is an intentional part of that screen's design. */
  headerDivider?: boolean;
  /** Lift-and-shadow hover affordance. Defaults on; set false for cards that
   * are static content sections rather than clickable/actionable surfaces
   * (e.g. investor profile sections). */
  hoverEffect?: boolean;
  /** `default` = `--shadow-card` (32px blur / 0.07 opacity). `soft` =
   * `--shadow-card-soft` (16px blur / 0.1 opacity) — the dashboard section
   * cards (Featured/Upcoming Events, Recent Activity) use this tighter shadow,
   * which does not match `--shadow-card` and isn't a hover state. */
  shadow?: 'default' | 'soft';
}

// The border WIDTH and the shadow are tokenised, not just their colours,
// because light and dark differ in which of the two defines the card's edge:
// light has no border at all and relies on the shadow (Tailwind Preflight
// zeroes border-width, which is what the pre-token Card depended on), while
// admin cards carry no shadow and the 1px border is their only edge. Tokenising
// the width keeps light at 0px, so no member screen shifts by 2px.
// `default` stays on the runtime CSS custom property (`--card-shadow`, themed
// per data-theme in index.css) so admin dark mode keeps overriding it. `soft`
// is dashboard-only (never rendered under an admin dark subtree) and can use
// the static `--shadow-card-soft` theme token directly via its generated
// `shadow-card-soft` utility.
const SHADOW_CLASSES: Record<NonNullable<CardProps['shadow']>, string> = {
  default: 'shadow-[var(--card-shadow)]',
  soft: 'shadow-card-soft',
};

function cardSurface(shadow: NonNullable<CardProps['shadow']>) {
  return `bg-surface-card border-[length:var(--card-border-width)] border-border-strong ${SHADOW_CLASSES[shadow]}`;
}

/**
 * White, rounded, shadowed container - the shared wrapper for every profile
 * section card (mnr-standards `common/` composite; used by 2+ features).
 */
export function Card({
  title,
  subtitle,
  action,
  headerDivider = false,
  hoverEffect = true,
  shadow = 'default',
  className = '',
  children,
  ...props
}: CardProps) {
  const bare = useContext(CardBareContext);
  const hasHeader = Boolean(title || subtitle || action);
  if (bare) {
    // Only the outermost card goes bare; a card nested inside the section's
    // form keeps its chrome.
    return (
      <div>
        <CardBareContext.Provider value={false}>{children}</CardBareContext.Provider>
      </div>
    );
  }

  return (
    <div
      className={`rounded-card p-8 transition-[transform,box-shadow] ${cardSurface(shadow)} ${hoverEffect ? 'hover:-translate-y-0.5 hover:shadow-[0_8px_40px_0_rgba(0,25,40,0.1)]' : ''} ${className}`.trim()}
      {...props}
    >
      {hasHeader && (
        <div className="mb-5 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              {title && <h2 className="text-h5 text-text-primary">{title}</h2>}
              {subtitle && <p className="mt-1 text-p6 text-text-secondary">{subtitle}</p>}
            </div>
            {action}
          </div>
          {headerDivider && <div className="h-px w-full bg-divider" />}
        </div>
      )}
      {children}
    </div>
  );
}
